import { validateEnumTranslations } from '../safety_checks';
import { PROJECT_CONFIG } from './project_branding/config';

export enum JwtExpiry {
  OneSecond = '1s',
  OneMinute = '1m',
  FifteenMinutes = '15m',
  ThirtyMinutes = '30m',
  OneHour = '1h',
  SevenDays = '7d',
  ThirtyDays = '30d',
}

export const controllerProfessionalAppApiPrefix = 'app/professional';
export const controllerProfessionalPublicApiPrefix = 'public/professional';
export const endpointRefreshTokenCookie = 'refresh';
export const pathRefreshCookie = `${controllerProfessionalAppApiPrefix}/${endpointRefreshTokenCookie}`;

// Mobile-only endpoints. They return the refresh_token in the body (so the
// native client can persist it in SecureStore). login/refresh/logout skip the
// captcha guard (called from native code — App Attest / Play Integrity will be
// wired in a follow-up). signup KEEPS the captcha: it is called from the
// register WebView, where the web fetcher already solves the Cap.js PoW.
export const endpointLoginMobile = 'mobile/login';
export const endpointRefreshMobile = 'mobile/refresh';
export const endpointLogoutMobile = 'mobile/logout';
export const endpointSignupMobile = 'mobile/signup';

export const controllerNotificationAppApiPrefix = 'app/notification';
export const endpointNotificationDevice = 'device';

// Politique de versions de l'app mobile (check bloquant au boot, cf.
// packages/mobile/src/lib/minVersion.ts). Constantes partagées client/serveur :
// un rename côté backend doit casser à la compilation, pas produire un 404 que
// le client fail-open avalerait en silence.
export const controllerMobileAppPublicApiPrefix = 'public/mobile-app';
export const endpointMobileAppMinVersion = 'min-version';

export const cookieLanguageFieldName = 'next-locale';

export enum Subcription {
  STARTER = 'STARTER',
  PRO = 'PRO',
  MAX = 'MAX',
}
export const SubscriptionRanking: Record<Subcription, number> = {
  [Subcription.STARTER]: 10,
  [Subcription.PRO]: 20,
  [Subcription.MAX]: 30,
};

//⚠️IMPACT miroir de l'enum Prisma `ProSubscriptionStatus` (schema.prisma).
// Sert au calcul de l'état d'accès partagé (access-utils.ts) côté backend ET front.
export enum ProSubscriptionStatus {
  ACTIVE = 'ACTIVE',
  PAST_DUE = 'PAST_DUE',
  SUSPENDED = 'SUSPENDED',
  CANCELED = 'CANCELED',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum CampaignType
export enum ProCampaignType {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum CampaignStatus
export enum ProCampaignStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  SENDING = 'SENDING',
  TREAT_SENDING = 'TREAT_SENDING',
  SENT = 'SENT',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum CampaignErrorStatus
export enum CampaignErrorStatus {
  NO_RECIPIENT_TO_SEND = 'NO_RECIPIENT_TO_SEND',
  INSUFFICIENT_SMS_QUOTA = 'INSUFFICIENT_SMS_QUOTA',
  INSUFFICIENT_EMAIL_QUOTA = 'INSUFFICIENT_EMAIL_QUOTA',
  SENDING_BLOCKED_DURING_TRIAL = 'SENDING_BLOCKED_DURING_TRIAL',
  PROVIDER_SEND_FAILED = 'PROVIDER_SEND_FAILED',
  // Code promo attaché invalide (ou variable code promo sans promo) : brouillon rendu au pro.
  CAMPAIGN_PROMO_BINDING_MISMATCH = 'CAMPAIGN_PROMO_BINDING_MISMATCH',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum pro_news_status
export enum ProNewsStatus {
  /** Article rédigé pour le pro (moteur SEO) : créneau réservé, publié seulement s'il le valide. */
  TO_REVIEW = 'TO_REVIEW',
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum pro_news_distribution_channel
export enum ProNewsDistributionChannel {
  GMB = 'GMB',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum pro_news_distribution_status
export enum ProNewsDistributionStatus {
  PENDING = 'PENDING',
  SENDING = 'SENDING',
  SENT = 'SENT',
  ERROR = 'ERROR',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum SyncGmbStatus
export enum SyncGoogleAccountType {
  GOOGLE_MY_BUSINESS = 'GOOGLE_MY_BUSINESS',
}

export const GoogleAccountTypeToType = {
  GOOGLE_MY_BUSINESS: [
    'https://www.googleapis.com/auth/business.manage',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/userinfo.profile',
    'openid',
  ],
};

export enum ReloadProduct {
  SMS_100 = 'SMS_100',
  SMS_500 = 'SMS_500',
  SMS_1000 = 'SMS_1000',
  SMS_5000 = 'SMS_5000',
  EMAIL_100 = 'EMAIL_100',
  EMAIL_1000 = 'EMAIL_1000',
  EMAIL_5000 = 'EMAIL_5000',
  EMAIL_25000 = 'EMAIL_25000',
  /**
   * Accélérateur référencement 3 mois (achat ponctuel sur le modèle des recharges, cf.
   * docs/to_implement/seo/seo-upsell-offers.md §4.3). Prix et contenu dans `TOPUPS`, pas ici :
   * `ReloadSmsContent` reste le catalogue des crédits SMS / e-mails.
   */
  SEO_BACKLINK_ACCELERATOR = 'SEO_BACKLINK_ACCELERATOR',
}

/** Produits de recharge qui créditent des SMS / e-mails (tout `ReloadProduct` sauf l'accélérateur). */
export type CreditReloadProduct = Exclude<ReloadProduct, ReloadProduct.SEO_BACKLINK_ACCELERATOR>;

export const ReloadSmsContent: Record<
  CreditReloadProduct,
  { quantity: number; type: ProCampaignType; price: number; popular?: boolean; best_value?: boolean }
> = {
  [ReloadProduct.SMS_100]: {
    type: ProCampaignType.SMS,
    quantity: 100,
    price: PROJECT_CONFIG.prices.SMS_100,
  },
  [ReloadProduct.SMS_500]: {
    type: ProCampaignType.SMS,
    quantity: 500,
    price: PROJECT_CONFIG.prices.SMS_500,
    popular: true,
  },
  [ReloadProduct.SMS_1000]: {
    type: ProCampaignType.SMS,
    quantity: 1000,
    price: PROJECT_CONFIG.prices.SMS_1000,
    best_value: true,
  },
  [ReloadProduct.SMS_5000]: {
    type: ProCampaignType.SMS,
    quantity: 5000,
    price: PROJECT_CONFIG.prices.SMS_5000,
  },
  [ReloadProduct.EMAIL_100]: {
    type: ProCampaignType.EMAIL,
    quantity: 100,
    price: PROJECT_CONFIG.prices.EMAIL_100,
  },
  [ReloadProduct.EMAIL_1000]: {
    type: ProCampaignType.EMAIL,
    quantity: 1000,
    price: PROJECT_CONFIG.prices.EMAIL_1000,
    popular: true,
  },
  [ReloadProduct.EMAIL_5000]: {
    type: ProCampaignType.EMAIL,
    quantity: 5000,
    price: PROJECT_CONFIG.prices.EMAIL_5000,
    best_value: true,
  },
  [ReloadProduct.EMAIL_25000]: {
    type: ProCampaignType.EMAIL,
    quantity: 25000,
    price: PROJECT_CONFIG.prices.EMAIL_25000,
  },
};

export const LIST_SMS_PRODUCTS = Object.keys(ReloadSmsContent).filter(
  (key) => ReloadSmsContent[key as keyof typeof ReloadSmsContent].type === ProCampaignType.SMS,
);
export const LIST_EMAIL_PRODUCTS = Object.keys(ReloadSmsContent).filter(
  (key) => ReloadSmsContent[key as keyof typeof ReloadSmsContent].type === ProCampaignType.EMAIL,
);

export const RELOAD_PRODUCT_LABEL_TRANSLATE_KEY = 'RELOAD_LABEL_';
export const RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY = 'RELOAD_DESCRIPTION_';

export const ReloadLabelTranslation = {
  'fr-FR': {
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.SMS_100}`]: 'Pack de 100 SMS',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.SMS_500}`]: 'Pack de 500 SMS',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.SMS_1000}`]: 'Pack de 1 000 SMS',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.SMS_5000}`]: 'Pack de 5 000 SMS',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.EMAIL_100}`]: 'Pack de 100 emails',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.EMAIL_1000}`]: 'Pack de 1 000 emails',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.EMAIL_5000}`]: 'Pack de 5 000 emails',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.EMAIL_25000}`]: 'Pack de 25 000 emails',
    [`${RELOAD_PRODUCT_LABEL_TRANSLATE_KEY}${ReloadProduct.SEO_BACKLINK_ACCELERATOR}`]:
      'Accélérateur référencement 3 mois',
  },
};
validateEnumTranslations(ReloadProduct, ReloadLabelTranslation, RELOAD_PRODUCT_LABEL_TRANSLATE_KEY, true);

export const ReloadDescriptionTranslation = {
  'fr-FR': {
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.SMS_100}`]:
      'Rechargez votre compte avec 100 SMS supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.SMS_500}`]:
      'Rechargez votre compte avec 500 SMS supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.SMS_1000}`]:
      'Rechargez votre compte avec 1 000 SMS supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.SMS_5000}`]:
      'Rechargez votre compte avec 5 000 SMS supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.EMAIL_100}`]:
      'Rechargez votre compte avec 100 emails supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.EMAIL_1000}`]:
      'Rechargez votre compte avec 1 000 emails supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.EMAIL_5000}`]:
      'Rechargez votre compte avec 5 000 emails supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.EMAIL_25000}`]:
      'Rechargez votre compte avec 25 000 emails supplémentaires pour vos campagnes et communications professionnelles.',
    [`${RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY}${ReloadProduct.SEO_BACKLINK_ACCELERATOR}`]:
      '3 articles par mois publiés sur des sites de votre secteur avec un lien vers votre site, pendant 3 mois à partir du 1er du mois suivant.',
  },
};
validateEnumTranslations(ReloadProduct, ReloadDescriptionTranslation, RELOAD_PRODUCT_DESCRIPTION_TRANSLATE_KEY, true);
